import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { RelatedChannels } from "@/components/calv/RelatedChannels";
import { CTABlock } from "@/components/calv/CTABlock";
import { Callout } from "@/components/calv/Callout";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/en/services/performance-marketing/linkedin-ads")({
  head: () => {
    const h = pageHead("pmLinkedinAds", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "LinkedIn Ads Agency",
          description: "Lead Gen Forms, Conversation Ads, ABM strategies — managed for pipeline contribution, not reach vanity.",
          url: "/en/services/performance-marketing/linkedin-ads",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Home", path: "/en" },
          { name: "Services", path: "/en/services" },
          { name: "Performance Marketing", path: "/en/services/performance-marketing" },
          { name: "LinkedIn Ads", path: "/en/services/performance-marketing/linkedin-ads" },
        ])),
      ],
    };
  },
  component: LinkedInAdsEN,
});

const steps = [
  { num: "01", title: "Pipeline Definition & Analysis (3–5 days)", body: "Before we touch a single LinkedIn campaign, we clarify pipeline logic. What is an MQL, what is an SQL, what is closed-won? Average conversion rates between stages? Customer LTV? Plus account audit if LinkedIn activity is already running, plus audience audit. Output: concrete hypotheses plus realistic expectation management." },
  { num: "02", title: "Setup, Tracking & ABM Lists (1–2 weeks)", body: "Hardening LinkedIn Insight Tag plus Conversions API for server-side tracking. CRM integration for pipeline attribution. Building ABM lists via Matched Audiences (account lists, contact lists) — often the biggest lever in B2B setups. Preparing first Lead Gen Form templates plus Conversation Ads sequences." },
  { num: "03", title: "Iteration & Optimization (from week 2–3)", body: "Iteration on audience sharpness, creative variation, form conversion rate. With B2B long sales cycles, lead quality assessment requires patience — we optimize not on weekly CPL but on pipeline contribution over multiple months. But operational management happens weekly." },
  { num: "04", title: "Scaling & Ongoing Operations (continuous)", body: "Budget scaling in working audience-creative combinations. ABM program expansion where it delivers pipeline. Quarterly sales-marketing alignment review. Operational management typically directly by the managing director." },
];

const capabilities = [
  { name: "Sponsored Content & Single-Image Ads", detail: "Hook variation · weekly cycles" },
  { name: "Lead Gen Forms", detail: "Pre-filled · CRM sync" },
  { name: "Conversation Ads", detail: "Demo booking · sales conversations" },
  { name: "Document Ads", detail: "Whitepapers · case studies" },
  { name: "Account-Based Marketing", detail: "Matched Audiences · tiered accounts" },
  { name: "Insight Tag & Conversions API", detail: "Server-side · CRM integration" },
  { name: "Audience Strategies", detail: "Job title · skill · industry" },
  { name: "Pipeline Reporting", detail: "MQL → SQL → closed-won" },
];

const audience = [
  { tag: "Primary", title: "B2B Lead Generation", body: "LinkedIn Ads is the most important performance channel for B2B setups with clearly defined audiences (company size, industry, function, seniority). Sensible from monthly budgets around €5,000, fully scalable to six-figure budgets." },
  { tag: "Secondary", title: "Enterprise SaaS", body: "Works for enterprise SaaS where sales cycles are long and individual leads have high lifetime value. Account-based marketing via company lists is often the most economical lever." },
  { tag: "Tertiary", title: "Recruiting & Employer Branding", body: "Can work when audiences are sharply defined and the company has a clear employer USP. For generic recruiting, profitability is rarely given." },
  { tag: "Disqualification", title: "When we're not the right partner", body: "For monthly budgets under €3,000. When the expectation is “massive volume of cheap leads” — LinkedIn delivers qualitative leads. When pipeline logic isn't defined." },
];

const realistic = [
  "Cost per MQL reduction of 25–40% within 2–3 months is realistic in most B2B setups when previously working with unsharp audience definitions or poorly maintained Lead Gen Forms.",
  "MQL-to-SQL conversion rate improvement of 30–60% is possible when audience sharpness is systematically increased — from “interesting industries” to concrete account lists plus contact lists.",
  "CPC reduction of 15–25% through audience optimization and creative iteration is a typical range. Unlike B2C platforms, CPC on LinkedIn is never the main goal — pipeline contribution is.",
  "ABM account penetration of 30–50% of target accounts within 6 months is typical when ABM is systematically deployed via Matched Audiences plus Sequenced Conversation Ads.",
];

const value = [
  ["Pipeline management instead of reach vanity.", "We report on MQL cost, SQL conversion, and pipeline contribution, not click-through rate or engagement rate."],
  ["ABM depth instead of spray-and-pray.", "Account-Based Marketing via Matched Audiences and tiered account logic — precise, not broad."],
  ["CRM integration as mandatory component.", "Conversions API plus CRM sync for pipeline attribution over long B2B sales cycles."],
  ["Realistic expectation management.", "We tell you honestly when LinkedIn Ads isn't the most economical first channel in your setup."],
  ["Operational management by experienced hand.", "In most mandates directly by the managing director."],
  ["Compensation model without budget coupling.", "We don't earn more when you spend more."],
];

const related = [
  { to: "/en/services/performance-marketing/google-ads", name: "Google Ads", sub: "Complementary bottom-funnel channel for B2B" },
  { to: "/en/services/picazzoo", name: "picazzoo", sub: "B2B-appropriate visual variation for Sponsored Content" },
  { to: "/en/services/tracking-data-foundation", name: "Tracking & Data Foundation", sub: "Pipeline attribution over long sales cycles" },
];

const channelLinks = [
  { to: "/en/services/performance-marketing/microsoft-ads", name: "Microsoft Advertising", sub: "Bing search, Edge and LinkedIn audiences — search-mix diversification next to Google." },
  { to: "/en/services/performance-marketing/meta-ads/recruiting", name: "Recruiting campaigns via Meta", sub: "Employee acquisition via Meta Ads — own funnel logic, own targeting discipline." },
];

function LinkedInAdsEN() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · LinkedIn Ads"
          headline={<>LinkedIn Ads Agency — <em>B2B Performance with Discipline,</em> not Reach Vanity.</>}
          lead="Lead Gen Forms, Conversation Ads, ABM strategies — managed for pipeline contribution, not vanity reach. Calvarius works as a LinkedIn Ads agency for B2B mandates, enterprise SaaS, and consulting-intensive service providers — with clean audience discipline and conversion tracking that reflects sales-cycle realities."
          metaStrip={["B2B Focus", "ABM Methodology", "CRM Integration"]}
          breadcrumbs={[
            { label: "Home", to: "/en" },
            { label: "Services", to: "/en/services" },
            { label: "Performance Marketing", to: "/en/services/performance-marketing" },
            { label: "LinkedIn Ads" },
          ]}
        />

        <Section number="02" kicker="Position" headline={<>What a LinkedIn Ads Agency really requires <em>today.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Anyone looking for a LinkedIn Ads agency has typically had two experiences: first, that LinkedIn is extremely expensive (CPCs between 8 and 25 euros are normal). Second, that platform recommendations often target awareness reach, not economically measurable lead generation. Anyone managing LinkedIn Ads via vanity reach burns budget. Anyone optimizing on click-through rate optimizes on appearance. Anyone managing on pipeline contribution enters the zone where LinkedIn Ads becomes economically viable for B2B.</p>
              <p className="mb-5">Calvarius treats LinkedIn Ads as a <strong className="font-semibold text-ink">pipeline tool</strong>, not a reach tool. That means: we define pipeline logic with the client first — what does a Marketing Qualified Lead cost, what does a Sales Qualified Lead cost, what does a closed-won customer cost? Only then is the campaign architecture built. We use Lead Gen Forms for friction-free capture. We deploy Conversation Ads where individualized contact initiation works. We manage account-based marketing lists via Matched Audiences and Company List Targeting — precise, not broad.</p>
              <p>Concretely, that means: CRM integration via Conversions API for clean pipeline attribution. LinkedIn Insight Tag plus server-side tracking for data quality. Clear separation between lead-volume campaigns and ABM campaigns — different logic, different management, different budget allocation. And realistic expectation management: in B2B setups, LinkedIn Ads is often not the most economical first channel, but the only channel that reaches certain audiences with high precision. When it makes economic sense, we clarify before the first euro invested via our <Link to="/en/services/tracking-data-foundation" className="underline">Tracking &amp; Data Foundation discipline</Link>.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Audience Fit" headline={<>When you need a LinkedIn Ads Agency <em>— and when you don't.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodology" headline={<>How our LinkedIn Ads Agency <em>starts mandates.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-10 max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7">
              <p className="eyebrow mb-3">Fast start as competitive advantage</p>
              <p className="text-[15px] leading-relaxed text-ink-2">While classic B2B agencies often need six to ten weeks before the first LinkedIn campaign with clean pipeline connection runs, we are productive within two to three weeks. This is possible because we don't sell pipeline logic as a workshop series, but clarify it as a prerequisite in the first conversation.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="What we deliver" headline={<>What our LinkedIn Ads Agency <em>concretely delivers.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Tooling" headline={<>Tools and stack of our <em>LinkedIn Ads management.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Native Platform</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>LinkedIn Campaign Manager</li>
                  <li>Sales Navigator (audience research)</li>
                  <li>Insight Tag &amp; Conversions API</li>
                  <li>Matched Audiences</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Tracking & CRM</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Server-side Conversions API</li>
                  <li>HubSpot, Salesforce, Pipedrive</li>
                  <li>Google Tag Manager (Web + Server)</li>
                  <li>Looker Studio pipeline dashboards</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Calvarius Tools</p>
                <ul className="space-y-3 text-sm text-ink-2">
                  <li><Link to="/en/services/picazzoo" className="font-medium text-ink hover:text-cyan-deep">picazzoo →</Link><br/><span className="text-ink-3">Brand-true visual variation for Sponsored Content</span></li>
                  <li><Link to="/en/services/tracking-data-foundation" className="font-medium text-ink hover:text-cyan-deep">Tracking & Data Foundation →</Link><br/><span className="text-ink-3">Pipeline attribution over long sales cycles</span></li>
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="What's realistic" headline={<>Realistic results of <em>LinkedIn Ads optimization.</em></>}
          caption="For B2B with long sales cycles, economic validation over several months is mandatory.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i+1).padStart(2,"0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="What you gain" headline={<>What you get from our <em>LinkedIn Ads Agency.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedChannels locale="en" items={channelLinks} />

        <Section variant="tinted" number="09" kicker="Go deeper" headline={<>Related disciplines.</>}
          caption="These topics work in interplay.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/en/services/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← All performance marketing disciplines
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Get concrete">
          <Callout>
            <strong>In strategic context:</strong> LinkedIn Ads is the performance tactic for paid B2B reach. Anyone viewing LinkedIn as a strategic channel — company visibility, managing-director presence, and paid activation as one system — will find the methodological framing under <Link to="/en/services/social-media-marketing" className="underline">Social Media Marketing →</Link>.
          </Callout>
          <CTABlock
            headline={<>LinkedIn Ads audit in 30 minutes — <em>free and non-binding.</em></>}
            body="In a first conversation, we clarify whether LinkedIn Ads makes economic sense in your B2B setup — and if so, with which budget magnitude and pipeline logic. You get an honest assessment."
            primary={{ label: "Schedule a conversation", to: "/en/contact" }}
            secondary={{ label: "Performance marketing overview", to: "/en/services/performance-marketing" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
